import { BillingType, UserType } from '../types/plan';

export type ContractType =
  | 'therapist-hours'
  | 'therapist-associado'
  | 'patient-constellation'
  | 'patient-participation';

interface ContractPlanLike {
  userType?: UserType;
  billingType?: BillingType;
  name?: string;
  sessionLabel?: string;
}

function normalize(value?: string | null): string {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim();
}

/** Deriva o tipo de contratação a partir dos dados do plano. */
export function getContractType(plan: ContractPlanLike): ContractType | null {
  const billing = normalize(plan.billingType);

  if (plan.userType === UserType.THERAPIST) {
    if (billing === 'hours') {
      return normalize(plan.name).includes('associado')
        ? 'therapist-associado'
        : 'therapist-hours';
    }
    return null;
  }

  if (plan.userType === UserType.PATIENT) {
    const label = normalize(plan.sessionLabel);
    if (label.includes('participacao')) return 'patient-participation';
    if (label.includes('constelacao')) return 'patient-constellation';
  }

  return null;
}

const GENERIC_SUCCESS_STEPS: string[] = [
  'Você receberá um e-mail com a confirmação da compra e o tutorial de uso.',
  'Acesse o painel de controle para verificar os seus benefícios.',
  'Se precisar de ajuda, fale com o suporte.',
];

const STEPS_BY_CONTRACT: Record<ContractType, string[]> = {
  'therapist-hours': [
    'Pagamento confirmado! As suas horas já estão disponíveis no seu painel.',
    'Enviamos para o seu e-mail o Guia de Horas Avulsas (PDF) com o passo a passo de configuração.',
    'Acesse o painel para acompanhar o seu saldo de horas e configurar a sua agenda.',
    'Quando quiser atender, entre na sua sala virtual pelo painel.',
  ],
  'therapist-associado': [
    'Pagamento confirmado! O seu Plano Associado já está ativo.',
    'Enviamos para o seu e-mail o Guia do Associado (PDF) com tudo o que você precisa.',
    'Configure o seu perfil e a sua vitrine pública no painel para começar a receber clientes.',
    'Acompanhe as suas horas, assinatura e histórico financeiro no painel.',
  ],
  'patient-constellation': [
    'Pagamento confirmado! A sua constelação está garantida.',
    'Você receberá um e-mail com a confirmação e os detalhes do seu horário.',
    'No dia, acesse a sala virtual pelo link enviado ou pelo seu painel.',
    'Se precisar remarcar ou tiver dúvidas, fale com o suporte.',
  ],
  'patient-participation': [
    'Pagamento confirmado! A sua participação está garantida.',
    'Você receberá um e-mail com a confirmação e as instruções de acesso.',
    'No horário da sessão, entre na sala virtual pelo link ou pelo seu painel.',
    'Qualquer dúvida, fale com o suporte.',
  ],
};

/** Retorna os passos de sucesso conforme o tipo de contratação (ou genéricos). */
export function getContractSuccessSteps(contractType?: ContractType | null): string[] {
  if (contractType && STEPS_BY_CONTRACT[contractType]) {
    return STEPS_BY_CONTRACT[contractType];
  }
  return GENERIC_SUCCESS_STEPS;
}
